import React, { useEffect, useMemo } from "react";
import { useState } from "react";
import styled from "styled-components";
import { stageArray2 } from "./State";
const stageLabels = {
  creation: 'Creation',
  cutting: 'Cutting',
  secendory_process: 'Secondary',
  embroidary: 'Embroidery',
  production: 'Production',
  packing: 'Packing',
  account: 'Billing',
  payment: 'Payment',
  dispatch: 'Dispatch',
  completed: 'Completed',
};
export default function ShowCount({ cards, selectedStage = null }) {
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [totalCards, setTotalCards] = useState(0);

  // Normalize incoming data (some endpoints return arrays, others may return paginated objects)
  const cardsArray = useMemo(() => {
    if (Array.isArray(cards)) return cards;
    if (cards && Array.isArray(cards?.results)) return cards.results;
    return [];
  }, [cards]);

  // If we are passed stagewise aggregated data, each stage appears only once,
  // so counting with `.length` will show `1` for each stage. Detect and handle that shape.
  const isStageSummary = useMemo(() => {
    return cardsArray.some(
      (item) =>
        item &&
        (Object.prototype.hasOwnProperty.call(item, "no_of_card") ||
          Object.prototype.hasOwnProperty.call(item, "noOfCard"))
    );
  }, [cardsArray]);

  const getSummaryCardsCount = (item) => {
    const raw = item?.no_of_card ?? item?.noOfCard ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const getSummaryQuantity = (item) => {
    const raw = item?.quantity ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  // Calculate stage data with memoization

// Helper to format stage names
const formatStageLabel = (stage) => {
  const specialCases = {
    secendory_process: 'Sec_Process',
    embroidary: 'Embroidery',
    account: 'Billing',
    designer: 'Design',
  };

  // if stage matches a special case, return that
  if (specialCases[stage]) return specialCases[stage];

  // else, make it Title Case (e.g. 'cutting' → 'Cutting', 'account' → 'Billing')
  return stage
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Map frontend stage filter to actual stage name in cards
const mapStageFilterToStage = (stage) => {
  if (!stage) return null;
  const stageMap = {
    creation: 'creation',
    cutting: 'cutting',
    secendory_process: 'secendory_process',
    embroidary: 'embroidary',
    production: 'production',
    packing: 'packing',
    account: 'account',
    dispatch: 'dispatch',
    designer: 'creation', // Designer maps to creation stage
    admin: 'creation', // Admin maps to creation stage
  };
  return stageMap[stage] || null;
};

const stageData = useMemo(() => {
  if (!cardsArray || !Array.isArray(cardsArray)) return [];

  // Check if a stage is actually selected (not null, undefined, or empty string)
  const hasSelectedStage = selectedStage && selectedStage !== null && selectedStage !== undefined && selectedStage !== '';
  
  // If API data is already aggregated by stage, use `no_of_card` and `quantity` directly.
  if (isStageSummary) {
    const stageSummaryMap = new Map();

    cardsArray.forEach((item) => {
      const stageKey = item?.current_stage;
      if (typeof stageKey !== "string" || stageKey.trim() === "") return;

      const prev = stageSummaryMap.get(stageKey) || { cards: 0, quantity: 0 };
      stageSummaryMap.set(stageKey, {
        cards: prev.cards + getSummaryCardsCount(item),
        quantity: prev.quantity + getSummaryQuantity(item),
      });
    });

    if (hasSelectedStage) {
      const mappedStage = mapStageFilterToStage(selectedStage);

      if (!mappedStage) {
        return stageArray2.map((stage) => {
          const summary = stageSummaryMap.get(stage) || { cards: 0, quantity: 0 };
          return {
            stage,
            label: formatStageLabel(stage),
            cards: summary.cards,
            quantity: summary.quantity,
          };
        });
      }

      return stageArray2.map((stage) => {
        if (stage === mappedStage) {
          const summary = stageSummaryMap.get(mappedStage) || { cards: 0, quantity: 0 };
          return {
            stage,
            label: formatStageLabel(selectedStage),
            cards: summary.cards,
            quantity: summary.quantity,
          };
        }

        return {
          stage,
          label: formatStageLabel(stage),
          cards: 0,
          quantity: 0,
        };
      });
    }

    return stageArray2.map((stage) => {
      const summary = stageSummaryMap.get(stage) || { cards: 0, quantity: 0 };
      return {
        stage,
        label: formatStageLabel(stage),
        cards: summary.cards,
        quantity: summary.quantity,
      };
    });
  }

  if (hasSelectedStage) {
    const mappedStage = mapStageFilterToStage(selectedStage);
    
    // If mapping failed, fall back to showing all stages (shouldn't happen, but safety check)
    if (!mappedStage) {
      return stageArray2.map((stage) => {
        const stageCards = cardsArray.filter((card) => card.current_stage === stage);
        const cardsCount = stageCards.length;
        const quantitySum = stageCards.reduce((sum, card) => sum + (parseInt(card.quantity) || 0), 0);
        return {
          stage,
          label: formatStageLabel(stage),
          cards: cardsCount,
          quantity: quantitySum,
        };
      });
    }
    
    // Count ALL cards returned from the API as belonging to the selected stage filter
    const allCardsCount = cardsArray.length;
    const allQuantitySum = cardsArray.reduce((sum, card) => sum + (parseInt(card.quantity) || 0), 0);
    
    return stageArray2.map((stage) => {
      // Only show data for the selected stage, show 0 for all others
      if (stage === mappedStage) {
        return {
          stage,
          label: formatStageLabel(selectedStage),
          cards: allCardsCount,
          quantity: allQuantitySum,
        };
      } else {
        // Show 0 for all other stages
        return {
          stage,
          label: formatStageLabel(stage),
          cards: 0,
          quantity: 0,
        };
      }
    });
  }

  // If no stage selected, show all stages with their actual data (by current_stage)
  return stageArray2.map((stage) => {
    const stageCards = cardsArray.filter((card) => card.current_stage === stage);
    const cardsCount = stageCards.length;
    const quantitySum = stageCards.reduce((sum, card) => sum + (parseInt(card.quantity) || 0), 0);

    return {
      stage,
      label: formatStageLabel(stage), // ✅ pretty name
      cards: cardsCount,
      quantity: quantitySum,
    };
  });
}, [cardsArray, selectedStage, isStageSummary]);

  useEffect(() => {
    if (!cardsArray || !Array.isArray(cardsArray)) return;

    // Exclude completed from totals (matches existing UI intent)
    const nonCompleted = cardsArray.filter((item) => item?.current_stage !== "completed");

    if (isStageSummary) {
      setTotalQuantity(nonCompleted.reduce((total, item) => total + getSummaryQuantity(item), 0));
      setTotalCards(nonCompleted.reduce((total, item) => total + getSummaryCardsCount(item), 0));
      return;
    }

    setTotalQuantity(nonCompleted.reduce((total, item) => total + (parseInt(item.quantity) || 0), 0));
    setTotalCards(nonCompleted.length);
  }, [cardsArray, selectedStage, isStageSummary]);

  return (
  <Container>
      <Grid>
        {stageData.map(({ stage, cards, quantity,label }) => (
          <Card key={stage}>
            <StageTitle>{label}</StageTitle>
            <MetricRow>
              <Metric>
                <MetricValue>{cards}</MetricValue>
                <MetricLabel>cards</MetricLabel>
              </Metric>
              <Divider />
              <Metric>
                <MetricValue>{quantity}</MetricValue>
                <MetricLabel>qty</MetricLabel>
              </Metric>
            </MetricRow>
          </Card>
        ))}

        <TotalCard>
          <StageTitle>Total</StageTitle>
          <MetricRow>
            <Metric>
              <MetricValue $isTotal>{totalCards}</MetricValue>
              <MetricLabel>cards</MetricLabel>
            </Metric>
            <Divider $isTotal />
            <Metric>
              <MetricValue $isTotal>{totalQuantity}</MetricValue>
              <MetricLabel>qty</MetricLabel>
            </Metric>
          </MetricRow>
        </TotalCard>
      </Grid>
    </Container>
  );
}

const Container = styled.div`
  padding: 2rem;
  margin: 0 auto;

  @media (max-width: 1200px) {
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 576px) {
    padding: 0.75rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.875rem;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 1.5rem 1rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6);
    border-radius: 12px 12px 0 0;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    border-color: #e0e0ff;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.08);

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 992px) {
    padding: 1.25rem 0.875rem;
    border-radius: 10px;
  }

  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
    border-radius: 8px;
  }

  @media (max-width: 576px) {
    padding: 1.25rem 1rem;
  }
`;

const TotalCard = styled(Card)`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;

  &::before {
    display: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(99, 102, 241, 0.25);
  }
`;

const StageTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 1rem;

  ${TotalCard} & {
    color: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 992px) {
    font-size: 0.72rem;
    margin-bottom: 0.875rem;
  }

  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 576px) {
    font-size: 0.75rem;
  }
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 0.75rem;

  @media (max-width: 576px) {
    gap: 0.5rem;
  }
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const MetricValue = styled.div`
  font-size: ${({ $isTotal }) => ($isTotal ? '1.5rem' : '1.25rem')};
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  margin-bottom: 0.35rem;
  word-break: break-all;
  text-align: center;
  max-width: 100%;
  overflow-wrap: break-word;

  ${TotalCard} & {
    color: #ffffff;
  }

  @media (max-width: 992px) {
    font-size: ${({ $isTotal }) => ($isTotal ? '1.35rem' : '1rem')};
  }

  @media (max-width: 768px) {
    font-size: ${({ $isTotal }) => ($isTotal ? '1.2rem' : '1.05rem')};
    margin-bottom: 0.25rem;
  }

  @media (max-width: 576px) {
    font-size: ${({ $isTotal }) => ($isTotal ? '1.35rem' : '1.15rem')};
  }
`;

const MetricLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${TotalCard} & {
    color: rgba(255, 255, 255, 0.8);
  }

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 40px;
  background: ${({ $isTotal }) => 
    $isTotal ? 'rgba(255, 255, 255, 0.3)' : '#e2e8f0'};
  flex-shrink: 0;

  @media (max-width: 768px) {
    height: 32px;
  }

  @media (max-width: 576px) {
    height: 36px;
  }
`;